import React, { useState } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { askAnalytics } from '../../services/analyticsAsk';
import { getMetric } from '../../services/analyticsMetric';
import { ORG_DASHBOARD_EVENTS, useOrgDashboardCapture } from '../../lib/analytics/orgDashboardEvents';
import { classifyAskError, AnalyticsAskResult } from './AnalyticsAskResult.jsx';
import { METRIC_CHIPS, WINDOWS, windowChipsFor, nextSelectedMetric, windowLabel, metricWindowLabel, betaBannerText, buildRetryCall, findChip, clarifyPickPayload } from './chipConfig.js';
import './analyticsAsk.css';

const SUGGESTIONS = ['Completion rate this month', 'How many active learners?', 'Average score this month', 'Improvement by attempt', 'Mastery rate this month'];

const INITIAL_STATE = { status: 'idle', result: null, selectedMetricForWindow: null, lastQuestion: '', lastRequest: null };

/** Owns the ask→render lifecycle for both the open box (NL /ask) and the deterministic
 *  chip path (/metric — no LLM). Mounted by the FAB drawer and the /insights page.
 *  Renders the server `presentation`/payload verbatim via the pure cards (AnalyticsAskResult).
 *
 *  State machine (`state`): { status, result, selectedMetricForWindow, lastQuestion, lastRequest }.
 *   - selectedMetricForWindow: the metric currently showing its window sub-chips + Cancel
 *     (null when no multi-window metric chip is mid-selection). Tapping a different metric
 *     chip resets it to the new metric; Cancel or any executed run clears it to null.
 *   - lastQuestion: the user's original TYPED question (set by ask()) — kept visible above
 *     the result while a clarify/nearest follow-up chip runs (`awaitingFollowUp`, a small
 *     UI-only flag alongside `state`, mirrors the existing `question` input-value pattern).
 *   - lastRequest: `{mode:'ask', question} | {mode:'metric', payload}` — set by EVERY submit
 *     path (ask() / runMetric()), so Retry (via the pure `buildRetryCall` in chipConfig.js)
 *     replays the exact same transport instead of always falling back to /ask (codex P1.1). */
export default function AnalyticsAsk({ onGenerateReport }) {
  const { appName } = useBranding();
  const captureOrgEvent = useOrgDashboardCapture();
  const [question, setQuestion] = useState('');
  const [state, setState] = useState(INITIAL_STATE);
  const [awaitingFollowUp, setAwaitingFollowUp] = useState(false);

  const isBusy = state.status === 'loading';

  // `typed` is true only from the Enter key and the Ask button (#4433). Retry and the
  // SUGGESTIONS buttons also call ask(), but those are not questions the facilitator typed.
  async function ask(q, { typed = false } = {}) {
    if (isBusy) return; // guard: no concurrent asks (Enter spam) — Gemini P1.2
    const text = (q ?? question).trim();
    if (!text) return;
    // #4258: a typed question was submitted. LENGTH ONLY: the question is learner-data
    // adjacent free text and never leaves the browser through telemetry. Chip taps
    // (runMetric), suggestion clicks and Retry are not typed questions and are not counted.
    if (typed) captureOrgEvent(ORG_DASHBOARD_EVENTS.ASK_PRAXIS_QUESTION_ASKED, { question_length: text.length });
    setQuestion(text);
    setAwaitingFollowUp(false);
    setState({
      status: 'loading', result: null, selectedMetricForWindow: null, lastQuestion: text,
      lastRequest: { mode: 'ask', question: text },
    });
    try {
      const result = await askAnalytics(text);
      setState((s) => ({ ...s, status: 'done', result }));
    } catch (err) {
      setState((s) => ({ ...s, status: classifyAskError(err), result: null }));
    }
  }

  // grain/scope/compare/preset (Phase 2 #1660-follow-on + #1694 Task 9): forwarded verbatim to
  // /metric when a caller supplies them (a scope-clarify pick, a template-metric suggestion chip,
  // a table follow-up chip, or a preset chip) — undefined for every caller that doesn't supply
  // one, so a plain {metric, window} call is unchanged. `ranking` (Phase 3 #1666b Task 6 / human-
  // test round 1) is forwarded the same way — a learner-ranking suggestion chip (chipConfig.js) or
  // a table's server-authored ranking_follow_ups chip both need it to survive the round-trip,
  // exactly like compare/preset above.
  async function runMetric({ metric, window, grain, scope, compare, preset, ranking, source, question: q }) {
    if (isBusy) return;
    setState((s) => ({
      ...s, status: 'loading', result: null, selectedMetricForWindow: null,
      lastRequest: { mode: 'metric', payload: { metric, window, grain, scope, compare, preset, ranking, source, question: q } },
    }));
    try {
      const result = await getMetric({ metric, window, grain, scope, compare, preset, ranking, source, question: q });
      setState((s) => ({ ...s, status: 'done', result }));
    } catch (err) {
      // codex P1.2: classify like the ask path so a real FEATURE_DISABLED (403) from
      // /api/analytics/metric renders the calm disabled state, not the generic transient error.
      setState((s) => ({ ...s, status: classifyAskError(err), result: null }));
    }
  }

  // codex P1.1: Retry must replay the SAME transport the failed request used — a deterministic
  // /metric call (chip/window/clarify/nearest) must retry through /metric, not through /ask with
  // the chip's rendered label as a freeform question. buildRetryCall is a pure mapping so this
  // stays statically testable without rendering the container.
  function retry() {
    const call = buildRetryCall(state.lastRequest);
    if (!call) return;
    if (call.mode === 'metric') runMetric(call.payload);
    else ask(call.question);
  }

  // A direct metric/window chip tap is a fresh exploration, not tied to a prior typed
  // question — clear the follow-up breadcrumb so it doesn't linger over an unrelated answer.
  function handleMetricChipTap(chip) {
    if (isBusy) return;
    setAwaitingFollowUp(false);
    if (!chip.windows) {
      setQuestion(chip.label);
      // `chip.window`/`chip.grain` (Phase 2 #1660-follow-on): the two template-metric suggestion
      // chips (stuck_learners, hardest_scenario) carry a FIXED window unlike `overview`, which has
      // no window at all (chip.window is undefined there — `?? null` preserves the original
      // window:null overview behaviour byte-for-byte). `chip.ranking`/`chip.compare` (Phase 3
      // human-test round 1, #1666/#1667): the learner-ranking + window-comparison suggestion
      // chips carry these instead of/alongside grain — undefined for every earlier one-tap chip,
      // so their calls are byte-identical to before.
      runMetric({ metric: chip.metric, window: chip.window ?? null, grain: chip.grain, ranking: chip.ranking, compare: chip.compare });
      return;
    }
    setState((s) => ({ ...s, selectedMetricForWindow: nextSelectedMetric(chip.metric) }));
  }

  function handleWindowChipTap(metric, windowKey) {
    if (isBusy) return;
    setAwaitingFollowUp(false);
    setQuestion(metricWindowLabel(metric, windowKey));
    runMetric({ metric, window: WINDOWS[windowKey].window });
  }

  function handleCancelWindow() {
    setState((s) => ({ ...s, selectedMetricForWindow: null }));
  }

  // Clarify/nearest/table follow-ups keep the original typed question visible above the result
  // while the follow-up loads and while its answer is shown (gemini P2.6).
  function handleClarifyPick(option) {
    if (isBusy) return;
    setAwaitingFollowUp(true);
    setQuestion(option.label);
    // clarifyPickPayload (chipConfig.js, Phase 2 #1660-follow-on): a scope/entity clarify option
    // is now an opaque body that may carry `scope` (and, in principle, `grain`) alongside
    // `metric`/`window` — must survive the pick, not collapse back to the pre-Phase-2 shape.
    runMetric(clarifyPickPayload(option));
  }

  function handleNearestTap(nearest) {
    if (isBusy) return;
    setAwaitingFollowUp(true);
    setQuestion(nearest.label);
    runMetric({ metric: nearest.metric, window: nearest.window, source: 'nearest_offer', question: state.lastQuestion });
  }

  // chip is {label, body} — the server-authored label IS the breadcrumb (#1692: no more
  // generic findChip fallback showing "Completion rate" for "Rank by completion rate instead").
  function handleTableFollowUp(chip) {
    if (isBusy) return;
    setAwaitingFollowUp(true);
    setQuestion(chip.label ?? findChip(chip.body?.metric)?.label ?? chip.body?.metric);
    runMetric(chip.body);
  }

  // Preset chips are definition toggles on the CURRENT card, not new questions — the input box
  // is untouched (gemini plan-review P1.3). source:'preset_chip' is sent future-proofing only:
  // Task 2 found analytics_metric_requests.source is CHECK-constrained (migration 0340, already
  // run on prod) so backend capture is DEFERRED (this slice ships no migrations; codex review
  // sanctioned tests-as-the-validation-layer). The route ignores unknown sources harmlessly.
  function handlePresetChange(chip) {
    if (isBusy) return;
    setAwaitingFollowUp(true);
    runMetric({ ...chip.body, source: 'preset_chip', question: `${chip.body.metric}:${chip.body.preset}` });
  }

  const { status, result, selectedMetricForWindow, lastQuestion } = state;
  const activeWindows = windowChipsFor(selectedMetricForWindow);

  return (
    <div className="aa-panel">
      <div className="aa-ask">
        <input className="aa-ask__input" placeholder={`Ask ${appName} about your learners…`}
          value={question} onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(undefined, { typed: true }); }} />
        <button type="button" className="aa-ask__send" onClick={() => ask(undefined, { typed: true })} disabled={!question.trim() || isBusy}>Ask</button>
      </div>
      <p className="aa-beta">{betaBannerText(appName)}</p>
      <div className="aa-suggests">
        {SUGGESTIONS.map((s) => <button key={s} type="button" className="aa-sug" onClick={() => ask(s)} disabled={isBusy}>{s}</button>)}
      </div>
      <div className="aa-chips">
        {METRIC_CHIPS.map((c) => (
          // Phase 3 ranking/compare chips (human-test round 1) share metric:'avg_score' with the
          // original "Average score" chip, so `c.metric` alone is no longer a unique key — `c.id`
          // (chipConfig.js) disambiguates; every earlier chip has no `id` so falls back to its
          // (still-unique-among-them) metric. The active/highlight class is gated on `c.windows`
          // too, not just the metric match — only a chip that can OPEN a window sub-menu is ever
          // "selected, awaiting a window pick"; a one-tap ranking/compare chip sharing the same
          // metric must never light up just because a sibling chip's window menu is open.
          <button key={c.id ?? c.metric} type="button"
            className={`aa-chip${c.windows && selectedMetricForWindow === c.metric ? ' aa-chip--active' : ''}`}
            onClick={() => handleMetricChipTap(c)} disabled={isBusy}>
            {c.label}
          </button>
        ))}
      </div>
      {activeWindows && (
        <div className="aa-window-chips">
          {activeWindows.map((wk) => (
            <button key={wk} type="button" className="aa-chip aa-chip--window"
              onClick={() => handleWindowChipTap(selectedMetricForWindow, wk)} disabled={isBusy}>
              {windowLabel(wk)}
            </button>
          ))}
          <button type="button" className="aa-chip aa-chip--cancel" onClick={handleCancelWindow} disabled={isBusy}>Cancel</button>
        </div>
      )}
      {lastQuestion && awaitingFollowUp && (
        <p className="aa-last-question">You asked: “{lastQuestion}”</p>
      )}
      <div className="aa-result-zone">
        <AnalyticsAskResult
          status={status} result={result} onGenerateReport={onGenerateReport} onRetry={retry}
          onClarifyPick={handleClarifyPick} onNearestTap={handleNearestTap}
          onSuggestionPick={handleMetricChipTap} suggestions={METRIC_CHIPS}
          onTableFollowUp={handleTableFollowUp} onTablePresetChange={handlePresetChange}
        />
      </div>
    </div>
  );
}
